import { useCallback, useEffect, useRef, useState } from "react";
import type { BildAusListe } from "./lib/api.ts";
import {
  alsClipPath,
  ausGeste,
  HINWEIS_NOTAUSGANG,
  huellrechteck,
  MINDESTABSTAND_PX,
  RECHTECK,
} from "./lib/umriss.ts";
import type { Punkt, Umriss } from "../shared/umriss.ts";
import Personenzahl, { FRAGE_PERSONEN } from "./Personenzahl.tsx";
import { HINWEIS_ZAHL_FEHLT, kannWeiter, startwert } from "./lib/personenwahl.ts";

/**
 * Schritt 3 aus `PROTOTYP-PLAN.md`: einmal locker um die Person herumziehen.
 *
 * Vollbild, weil hier gezeichnet und nicht verglichen wird (ARCHITEKTUR.md 6.2). Eine
 * Geste je Bild, kein Zoom, keine nachziehbaren Punkte: auf 390 px trifft man sie ohne
 * Zoom nicht, und Zoom gibt es im Bearbeiten nicht mehr (D2). Korrektur ist **noch
 * einmal herumziehen** — das überschreibt.
 *
 * Kein Canvas. Während des Ziehens eine SVG-Linie über dem Bild, nach dem Loslassen
 * `clip-path` auf dem `<img>`. Das läuft auf der GPU und gilt unverändert für die
 * Kachel, diesen Bildschirm und später den Export (6.2).
 *
 * **Seit Schritt 5 wird hier auch die Personenzahl erfasst.** Der Ablauf in 6.2 sieht sie
 * genau an dieser Stelle vor („grob drumherum ziehen → Personenzahl angeben → das Element
 * erscheint in der Reihe"), und es ist der einzige Moment, in dem das Foto groß genug ist,
 * um überhaupt zu zählen: in der Reihe ist eine Figur im Mittel 47 px breit (gemessen am
 * 24.09.2026). Die Zeile steht **dauerhaft** im Fuß und nicht erst nach dem Umriss —
 * sonst schrumpfte die Zeichenfläche in dem Augenblick, in dem der frisch gezogene Umriss
 * erscheint, und das Bild spränge unter dem Finger weg.
 */

interface Props {
  bild: BildAusListe;
  /** „Foto 3 von 12" — die Warteschlange zählt, nicht die Kundschaft. */
  nummer: number;
  gesamt: number;
  /** Umriss **und** Personenzahl zusammen: beides gehört zu demselben Handgriff, und
   *  wer „Weiter" tippt, hat beides angegeben (Schritt 5). */
  onSpeichern: (umriss: Umriss, personen: number) => void;
  /** Raus, ohne etwas zu setzen — der Ausgang, den dieser Bildschirm bis zum 29.09.2026
   *  nicht hatte. Was hier gezogen wurde, ist damit verworfen; gespeichert wird allein
   *  über „Weiter". Erreichbar, **solange kein Umriss dasteht** — liegt einer da, trägt
   *  derselbe Platz das Löschen (siehe unten am Knopf). */
  onZurueck: () => void;
}

/**
 * Ab welcher Fensterhöhe die Bedienelemente **neben** das Foto wandern statt darunter.
 *
 * ⚠️ **Gesetzt, nicht gemessen.** 500 px trennen die Fälle, um die es geht: ein Telefon
 * hochkant hat 667 bis 932 px Höhe, quer gedreht 390 bis 430. Ein Tablet quer liegt
 * darüber und behält die Leiste unten, wo genug Platz ist.
 */
const KNAPPE_HOEHE_PX = 500;

/**
 * Ist das Fenster zu flach für eine Leiste unter dem Bild?
 *
 * ⚠️ **Der Grund, warum es diese Unterscheidung gibt, ist gemessen** (24.09.2026): quer
 * bekommt ein Gruppenfoto nur 309 × 150 px Zeichenfläche, weil Kopfzeile, Hinweis,
 * Personenzeile und zwei 56-px-Knöpfe zusammen mehr als die halbe Fensterhöhe fressen.
 * Eine einzelne Person darin ist rund 40 px breit — das ist die Sorte Bildschirm, an der
 * jemand aufgibt (PROTOTYP-PLAN, Teilfrage 1).
 *
 * **Daneben und nicht darüber.** Ein Overlay über dem Bild wäre die naheliegende Lösung
 * und die falsche: die Leiste läge am **unteren** Bildrand, und genau dort stehen die
 * Füße — die Kante, an der man beim Umfahren entlangfährt. Quer ist links und rechts vom
 * eingepassten Bild ohnehin Platz frei; dorthin gehören die Knöpfe.
 */
function useKnappeHoehe(): boolean {
  const [knapp, setKnapp] = useState(false);
  useEffect(() => {
    const abfrage = window.matchMedia(`(max-height: ${String(KNAPPE_HOEHE_PX)}px)`);
    const merken = () => {
      setKnapp(abfrage.matches);
    };
    merken();
    abfrage.addEventListener("change", merken);
    return () => {
      abfrage.removeEventListener("change", merken);
    };
  }, []);
  return knapp;
}

/** Wo das Bild in der Fläche wirklich liegt, in Pixeln der Fläche. */
interface Rahmen {
  links: number;
  oben: number;
  breite: number;
  hoehe: number;
}

/**
 * Das Bild mittig in die Fläche einpassen — von Hand gerechnet, nicht über
 * `object-fit: contain`.
 *
 * ⚠️ Der Unterschied ist tragend: bei `object-contain` bleibt die **Element**-Box so
 * groß wie die Fläche, und das Bild liegt mit Rändern darin. Prozentwerte in
 * `clip-path` rechnen aber gegen die Element-Box. Der Umriss säße damit um die
 * Randbreite verschoben — sichtbar falsch, und nur bei manchen Seitenverhältnissen.
 * Hier ist die Box **gleich** dem Bild, damit Prozent genau das heißt, was
 * `shared/umriss.ts` verspricht.
 */
function einpassen(flaeche: DOMRect, breite: number, hoehe: number): Rahmen | null {
  if (flaeche.width <= 0 || flaeche.height <= 0 || breite <= 0 || hoehe <= 0) return null;
  const skala = Math.min(flaeche.width / breite, flaeche.height / hoehe);
  const b = breite * skala;
  const h = hoehe * skala;
  return { links: (flaeche.width - b) / 2, oben: (flaeche.height - h) / 2, breite: b, hoehe: h };
}

export default function Umfahren({ bild, nummer, gesamt, onSpeichern, onZurueck }: Props) {
  const flaeche = useRef<HTMLDivElement>(null);
  const [rahmen, setRahmen] = useState<Rahmen | null>(null);
  const [pfad, setPfad] = useState<Punkt[]>([]);
  const [zeichnet, setZeichnet] = useState(false);
  const [umriss, setUmriss] = useState<Umriss | null>(bild.umriss);
  // Ohne Startwert für ein frisches Bild: eine vorgegebene 1 hat niemand angesehen
  // (Annas Test mit Eltern und Partner, 07.10.2026; `src/lib/personenwahl.ts`).
  const [personen, setPersonen] = useState<number | null>(() => startwert(bild));
  const [hinweis, setHinweis] = useState<string | null>(null);
  const knapp = useKnappeHoehe();

  // ⚠️ Es gibt hier **keinen** Effekt, der den Zustand auf ein neues Foto zurücksetzt.
  // Den Wechsel erledigt `key={bildId}` in `App.tsx`: React hängt die Komponente neu
  // auf, und der Zustand beginnt von vorn. Ein Effekt daneben täte dasselbe ein zweites
  // Mal — und würde beim Nachsignieren (D5) den gerade gezogenen Umriss überschreiben,
  // sobald die frische Liste denselben Datensatz mit altem `umriss` hereinreicht.

  // Die Seite darf nicht scrollen, solange dieser Bildschirm offen ist. `touch-action`
  // allein reicht dafür nicht: es regelt die Zeichenfläche, nicht den Rest der Seite.
  useEffect(() => {
    const vorher = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = vorher;
    };
  }, []);

  // Die Fläche ändert sich beim Drehen des Telefons und wenn die Adressleiste ein- und
  // ausfährt. Ein einmal gemessener Rahmen wäre danach falsch, und der Umriss läge
  // neben der Person.
  useEffect(() => {
    const element = flaeche.current;
    if (!element) return;
    const messen = () => {
      setRahmen(einpassen(element.getBoundingClientRect(), bild.breite, bild.hoehe));
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(element);
    return () => {
      beobachter.disconnect();
    };
  }, [bild.breite, bild.hoehe]);

  const punktAus = useCallback((event: React.PointerEvent<HTMLDivElement>): Punkt => {
    const box = event.currentTarget.getBoundingClientRect();
    return [event.clientX - box.left, event.clientY - box.top];
  }, []);

  function zeichnenBeginnt(event: React.PointerEvent<HTMLDivElement>) {
    // Ohne das verliert ein schneller Finger den Pfad, sobald er die Box verlässt
    // (ARCHITEKTUR.md 2.2). In wizard-flow war genau das der teure Fehler.
    //
    // In `try` gefasst, weil `setPointerCapture` wirft, wenn der Zeiger zwischen
    // Ereignis und Aufruf schon wieder weg ist. Ungefangen risse das die ganze Geste
    // ab — und das Fangen ist eine Verbesserung, keine Bedingung: ohne Capture zeichnet
    // es weiter, nur verliert ein sehr schneller Finger den Pfad am Rand.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Absichtlich still. Ein deutscher Fehlertext wäre hier falsch: für die
      // Kundschaft ist nichts passiert.
    }
    setZeichnet(true);
    setHinweis(null);
    setUmriss(null);
    setPfad([punktAus(event)]);
  }

  function zeichnenLaeuft(event: React.PointerEvent<HTMLDivElement>) {
    if (!zeichnet) return;
    const punkt = punktAus(event);
    setPfad((bisher) => {
      const letzter = bisher[bisher.length - 1];
      // Schon beim Sammeln ausdünnen, nicht erst am Ende: eine Wischgeste liefert
      // sonst über tausend Punkte, und jeder davon wäre ein React-Zustandswechsel.
      if (letzter && Math.hypot(punkt[0] - letzter[0], punkt[1] - letzter[1]) < MINDESTABSTAND_PX) {
        return bisher;
      }
      return [...bisher, punkt];
    });
  }

  function zeichnenEndet() {
    if (!zeichnet) return;
    setZeichnet(false);
    const fertig = rahmen ? ausGeste(pfad, rahmen.breite, rahmen.hoehe) : null;
    setPfad([]);
    if (fertig) {
      setUmriss(fertig);
      return;
    }
    // Der Notausgang: eine misslungene Geste darf nicht in einem leeren Element enden
    // (ARCHITEKTUR.md 6.2). Also Rechteck, und dazu ein Satz, der sagt, was los ist.
    setUmriss(RECHTECK);
    setHinweis(HINWEIS_NOTAUSGANG);
  }

  /**
   * Den gezogenen Umriss wegnehmen (Carolins Entscheidung, 29.09.2026).
   *
   * Nochmal herumziehen überschreibt zwar ohnehin — aber das setzt voraus, dass man weiß,
   * wo man neu anfangen will. Wer einen Umriss sieht, der nicht passt, will ihn erst
   * **weg** haben und das ganze Foto wieder sehen. Nebenbei geht „Weiter" damit wieder
   * aus: ein Umriss, den man verworfen hat, lässt sich nicht mehr versehentlich
   * speichern.
   *
   * Nur der Zustand dieses Bildschirms. Ein **gespeicherter** Umriss bleibt, bis „Weiter"
   * einen neuen schickt — wer hier löscht und dann abbricht, hat nichts verloren.
   */
  function umrissLoeschen() {
    setPfad([]);
    setZeichnet(false);
    setHinweis(null);
    setUmriss(null);
  }

  const boden = umriss ? huellrechteck(umriss).unten : null;

  return (
    <div className={`fixed inset-0 z-10 flex bg-neutral-900 ${knapp ? "flex-row" : "flex-col"}`}>
      {!knapp && (
        <p className="px-4 pt-4 pb-2 text-center text-sm font-medium text-neutral-100">
          Foto {String(nummer)} von {String(gesamt)}
        </p>
      )}

      {/* `min-w-0` gehört dazu, sobald daneben etwas steht: ohne es wäre die
          Mindestbreite eines Flex-Elements seine Inhaltsbreite, und die Fläche
          drängte die Spalte aus dem Bild. Dieselbe Falle wie in `src/Reihe.tsx`,
          nur andersherum. */}
      <div ref={flaeche} className="relative min-h-0 min-w-0 flex-1">
        {rahmen && (
          <div
            className="absolute touch-none select-none"
            style={{
              left: rahmen.links,
              top: rahmen.oben,
              width: rahmen.breite,
              height: rahmen.hoehe,
              overscrollBehavior: "contain",
              WebkitTapHighlightColor: "transparent",
            }}
            onPointerDown={zeichnenBeginnt}
            onPointerMove={zeichnenLaeuft}
            onPointerUp={zeichnenEndet}
            // Ein abgebrochener Zeiger — eingehender Anruf, Systemgeste — muss dasselbe
            // auslösen wie ein Loslassen. Sonst bliebe `zeichnet` stehen und der
            // nächste Fingertipp zeichnete an der alten Linie weiter.
            onPointerCancel={zeichnenEndet}
          >
            <img
              src={bild.vorschau}
              alt=""
              draggable={false}
              className="h-full w-full"
              style={umriss ? { clipPath: alsClipPath(umriss) } : undefined}
            />
            {/* Ohne Umriss und ohne laufende Geste liegt nichts über dem Bild — die
                Kundschaft sieht das Foto, sonst nichts. */}
            {pfad.length > 1 && (
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox={`0 0 ${String(rahmen.breite)} ${String(rahmen.hoehe)}`}
                preserveAspectRatio="none"
              >
                <polyline
                  points={pfad.map(([x, y]) => `${String(x)},${String(y)}`).join(" ")}
                  fill="none"
                  stroke="white"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {/* Die Bodenlinie erscheint erst nach dem Loslassen. Sie ist die einzige
                Stelle, an der Hinsehen sich lohnt (6.2) — und sie erklärt die Regel
                für Schritt 4 durchs Sehen statt durch Text. */}
            {boden !== null && !zeichnet && (
              <div
                className="pointer-events-none absolute right-0 left-0 h-0.5 bg-amber-400"
                style={{ top: `${String(boden)}%` }}
              />
            )}
          </div>
        )}
      </div>

      <div
        className={
          knapp
            ? "flex w-56 shrink-0 flex-col gap-2 overflow-y-auto px-3 py-3"
            : "flex flex-col gap-3 px-4 pt-2 pb-6"
        }
      >
        {knapp && (
          <p className="text-center text-sm font-medium text-neutral-100">
            Foto {String(nummer)} von {String(gesamt)}
          </p>
        )}
        <p
          aria-live="polite"
          className={
            knapp ? "text-sm text-neutral-200" : "min-h-10 text-center text-sm text-neutral-200"
          }
        >
          {hinweis ??
            (umriss
              ? "Hier stehen die Füße. Passt es nicht? Fahr einfach nochmal drumherum."
              : "Fahr grob um die Person herum.")}
        </p>
        {/* Die Frage steht auch vor dem ersten Strich da. Sie kostet keine Erklärung —
            und ihre Höhe geht der Zeichenfläche einmal verloren statt mittendrin. */}
        {/* Hervorgehoben, sobald der Umriss steht — der Augenblick, in dem der Blick
            vom Foto kommt und die Frage überhaupt gelesen wird (Annas Test, 29.09.2026).
            Die Zeile steht trotzdem schon **vorher** da: ihre Höhe geht der Zeichenfläche
            einmal am Anfang verloren statt mitten in der Geste. */}
        <Personenzahl
          wert={personen}
          onAendern={(wert) => {
            setPersonen(wert);
            if (hinweis === HINWEIS_ZAHL_FEHLT) setHinweis(null);
          }}
          frage={FRAGE_PERSONEN}
          stapeln={knapp}
          betont={umriss !== null && !zeichnet}
        />
        <div className={knapp ? "flex flex-col gap-2" : "flex gap-3"}>
          {/* ⚠️ **Hier stand bis zum 29.09.2026 „Lieber ein Rechteck"** (Carolins
              Entscheidung nach Annas Test). Der Knopf setzte den Umriss auf das ganze
              Foto und machte „Weiter" damit aktiv — ein Rechteck war also eine
              angebotene **Wahl**, und gewollt ist sie nicht: ein Rechteck lässt den
              Hintergrund stehen, und die Vorschau sieht dann aus wie eine Fotocollage
              statt wie eine Zeichnung (ARCHITEKTUR.md 6.2).

              Der Rückfall selbst bleibt — aber nur noch als **Notausgang** für eine
              misslungene Geste (`zeichnenEndet`), mit einem Satz, der sagt, was
              passiert ist. Niemand wählt ihn mehr, jeder wird von ihm aufgefangen.

              **Ein Platz, zwei Aufgaben — und immer die, die gerade zu haben ist:**

              - **kein Umriss da → „Abbrechen"**, der Ausgang. „Weiter" ist ohne Umriss
                aus; wer versehentlich ein Foto öffnete, musste bis zum 29.09. eine
                Entscheidung treffen, obwohl er nur nachsehen wollte (`TODO.md`, 24.09.)
              - **ein Umriss da → „Umriss löschen"** (Carolin, 29.09.). Nochmal
                herumziehen überschreibt zwar ohnehin — aber wer einen Umriss sieht, der
                nicht passt, will ihn erst **weg** haben und das ganze Foto wieder sehen

              ⚠️ **Deshalb wechselt die Beschriftung, statt dass zwei Knöpfe nebeneinander
              stehen.** Drei Knöpfe in dieser Reihe brächen auf 390 px um, und diese
              Leiste ist genau die, deren Höhe der Zeichenfläche verloren geht (gemessen
              am 24.09.2026). Der Wechsel ist vertretbar, weil der Zustand **sichtbar**
              ist: entweder liegt ein Umriss über dem Foto oder nicht, und der Knopf sagt
              jeweils, was er gerade tut. Mit Umriss ist der Ausgang zwei Tipps weit
              (löschen, dann abbrechen) — der seltenere Weg trägt den Aufschlag. */}
          <button
            type="button"
            onClick={umriss ? umrissLoeschen : onZurueck}
            className="min-h-14 flex-1 rounded-xl border border-neutral-400 px-4 text-base font-medium text-neutral-100"
          >
            {umriss ? "Umriss löschen" : "Abbrechen"}
          </button>
          {/* Ohne Umriss ist „Weiter" wirklich aus — dann steht „Fahr grob um die Person
              herum." ohnehin da. Fehlt nur die Zahl, sieht der Knopf aus wie aus, nimmt
              den Tipp aber an und sagt, was fehlt (`aria-disabled` statt `disabled`):
              ein grauer Knopf, der stumm bleibt, wirkt wie eine kaputte Seite
              (Usability-Review, 08.10.2026). */}
          <button
            type="button"
            onClick={() => {
              if (umriss && personen !== null) onSpeichern(umriss, personen);
              else if (umriss) setHinweis(HINWEIS_ZAHL_FEHLT);
            }}
            disabled={!umriss}
            aria-disabled={!kannWeiter(umriss, personen)}
            className={`min-h-14 flex-1 rounded-xl px-4 text-base font-medium ${
              kannWeiter(umriss, personen)
                ? "bg-white text-neutral-900"
                : "bg-neutral-600 text-neutral-400"
            }`}
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
}
